import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import BASE_URL from "../config";
import { FaMapMarkerAlt, FaMapPin, FaCar } from "react-icons/fa";
import { Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import io from 'socket.io-client';

// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icons for different markers
const createIcon = (color, icon = null) => {
    return L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">${icon || ''}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
    });
};

const pickupIcon = createIcon('#28a745', '📍'); // Green with pin icon
const dropIcon = createIcon('#dc3545', '🏁');   // Red with flag icon
const driverIcon = createIcon('#007bff', '🚗'); // Blue with car icon

// Component to fit map bounds to show all markers
const FitBounds = ({ bounds }) => {
    const map = useMap();
    
    useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [bounds, map]);
    
    return null;
};

const PublicTrackingPage = () => {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [driverLocation, setDriverLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const socketRef = useRef(null);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/public/orders/${orderId}`);
                setOrder(response.data.data);
                
                // Initialize socket connection
                socketRef.current = io(BASE_URL, {
                    transports: ['websocket']
                });
                
                socketRef.current.on('connect', () => {
                    console.log('Connected to WebSocket server');
                    socketRef.current.emit('join_tracking', { order_id: orderId });
                });
                
                socketRef.current.on('driver_location_update', (data) => {
                    if (data.order_id == orderId) {
                        setDriverLocation({
                            lat: parseFloat(data.lat),
                            lng: parseFloat(data.lng),
                            timestamp: data.timestamp
                        });
                    }
                });
                
                setLoading(false);
            } catch (err) {
                console.error("Error fetching order details:", err);
                setError("Order not found or tracking link is invalid");
                setLoading(false);
            }
        };

        fetchOrderDetails();

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, [orderId]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="alert alert-danger">
                    {error}
                </div>
            </div>
        );
    }

    // Calculate bounds for map to fit all markers
    const calculateBounds = () => {
        const bounds = [];
        
        if (order.pickup_lat && order.pickup_lng) {
            bounds.push([parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]);
        }
        
        if (order.delivery_lat && order.delivery_lng) {
            bounds.push([parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)]);
        }
        
        if (driverLocation) {
            bounds.push([driverLocation.lat, driverLocation.lng]);
        }
        
        return bounds.length > 0 ? bounds : null;
    };

    const bounds = calculateBounds();

    return (
        <div className="container mt-4">
            <Card>
                <Card.Header className="bg-primary text-white">
                    <h4 className="mb-0">Order Tracking</h4>
                </Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <h5>Order Information</h5>
                            <p><strong>Order #:</strong> {order.order_number}</p>
                            <p><strong>Status:</strong> {order.status}</p>
                            <p><strong>Customer:</strong> {order.customer_name}</p>
                        </Col>
                        <Col md={6}>
                            <h5>Driver Information</h5>
                            <p><strong>Name:</strong> {order.driver_name}</p>
                            <p><strong>Vehicle:</strong> {order.vehicle_details || 'N/A'}</p>
                        </Col>
                    </Row>
                    
                    <div style={{ height: '400px', width: '100%' }}>
                        {bounds ? (
                            <MapContainer 
                                center={bounds[0]} 
                                zoom={13} 
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                
                                {/* Pickup marker */}
                                <Marker 
                                    position={[order.pickup_lat, order.pickup_lng]} 
                                    icon={pickupIcon}
                                >
                                    <Popup>
                                        <strong>Pickup Location</strong><br />
                                        {order.pickup_address}
                                    </Popup>
                                </Marker>
                                
                                {/* Drop marker */}
                                <Marker 
                                    position={[order.delivery_lat, order.delivery_lng]} 
                                    icon={dropIcon}
                                >
                                    <Popup>
                                        <strong>Delivery Location</strong><br />
                                        {order.delivery_address}
                                    </Popup>
                                </Marker>
                                
                                {/* Driver marker */}
                                {driverLocation && (
                                    <Marker 
                                        position={[driverLocation.lat, driverLocation.lng]} 
                                        icon={driverIcon}
                                    >
                                        <Popup>
                                            <strong>Driver Location</strong><br />
                                            Last updated: {new Date(driverLocation.timestamp).toLocaleString()}
                                        </Popup>
                                    </Marker>
                                )}
                                
                                {/* Route line */}
                                <Polyline 
                                    positions={bounds} 
                                    color="#007bff" 
                                    weight={4} 
                                    opacity={0.7}
                                />
                                
                                {/* Fit bounds to show all markers */}
                                <FitBounds bounds={bounds} />
                            </MapContainer>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                                <p>No location data available</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="mt-3 text-center">
                        <small className="text-muted">
                            {driverLocation ? 
                                `Last updated: ${new Date(driverLocation.timestamp).toLocaleString()}` : 
                                'Waiting for driver location...'}
                        </small>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default PublicTrackingPage;